import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { clsx } from '../../ds/clsx';
import { Eyebrow } from '../../ds/components';
import { useEscapeKey, useFocusTrap, useOutsideClick } from '../../ds/hooks';
import type { QuickPick } from './api';
import {
  WEEKDAY_SHORT,
  isValidDayKey,
  isValidTime,
  longDay,
  monthGrid,
  monthLabel,
  normalizeTime,
  parseDayKey,
  parseGermanDate,
  shortDay,
  todayKey,
} from './dates';
import styles from './SnoozePopover.module.css';

export interface SnoozeValue {
  dueOn: string;
  dueTime: string | null;
}

export interface SnoozeFormProps {
  quickPicks: ReadonlyArray<QuickPick>;
  /** Preselected day (the task's due day) and time. */
  initialOn?: string | null | undefined;
  initialTime?: string | null | undefined;
  pending?: boolean | undefined;
  error?: string | null | undefined;
  /** Text of the primary button („Verschieben"). */
  submitLabel?: string | undefined;
  onCancel: () => void;
  onSubmit: (value: SnoozeValue) => void;
}

const DATE_REQUIRED = 'Bitte ein Datum wählen.';
const DATE_INVALID = 'Bitte ein Datum im Format TT.MM.JJJJ angeben.';
const TIME_INVALID = 'Bitte eine Uhrzeit im Format HH:MM angeben.';

/**
 * Content of the „Verschieben auf" popover (Task-Queue 02): four quick picks with their dates, the
 * month sheet, date and time fields, „Abbrechen" / „Verschieben". Also used inline in the mobile sheet
 * and in the queue's skip dialog („Lieber auf später legen?").
 */
export function SnoozeForm({
  quickPicks,
  initialOn,
  initialTime,
  pending = false,
  error,
  submitLabel = 'Verschieben',
  onCancel,
  onSubmit,
}: SnoozeFormProps) {
  const today = todayKey();
  const id = useId();
  const [dueOn, setDueOn] = useState<string>(initialOn ?? '');
  const [dateText, setDateText] = useState<string>(initialOn ? longDay(initialOn) : '');
  const [dueTime, setDueTime] = useState<string>(initialTime ?? '');
  const [message, setMessage] = useState<string | null>(null);
  const viewStart = isValidDayKey(dueOn) ? dueOn : today;
  const [view, setView] = useState(() => {
    const { year, month } = parseDayKey(viewStart);
    return { year, month };
  });

  useEffect(() => {
    if (!isValidDayKey(dueOn)) return;
    const { year, month } = parseDayKey(dueOn);
    setView({ year, month });
    setDateText(longDay(dueOn));
  }, [dueOn]);

  function changeDateText(text: string) {
    setDateText(text);
    const key = parseGermanDate(text);
    if (key !== null) setDueOn(key);
  }

  function shiftMonth(delta: number) {
    setView((current) => {
      const index = current.year * 12 + (current.month - 1) + delta;
      return { year: Math.floor(index / 12), month: (index % 12) + 1 };
    });
  }

  function submit() {
    if (pending) return;
    const typed = parseGermanDate(dateText);
    const on = typed ?? (dateText.trim() === '' ? '' : dueOn);
    if (typed === null && dateText.trim() !== '') {
      setMessage(DATE_INVALID);
      return;
    }
    if (!isValidDayKey(on)) {
      setMessage(DATE_REQUIRED);
      return;
    }
    const time = normalizeTime(dueTime);
    if (time !== '' && !isValidTime(time)) {
      setMessage(TIME_INVALID);
      return;
    }
    setMessage(null);
    onSubmit({ dueOn: on, dueTime: time === '' ? null : time });
  }

  const cells = monthGrid(view.year, view.month);

  return (
    <div className={styles.form}>
      <Eyebrow size={11}>Verschieben auf</Eyebrow>
      <div className={styles.picks}>
        {quickPicks.map((pick) => {
          const selected = pick.on !== null && pick.on === dueOn;
          return (
            <button
              key={pick.key}
              type="button"
              className={clsx(styles.pick, selected && styles.pickSelected)}
              aria-pressed={selected}
              onClick={() => {
                if (pick.on !== null) setDueOn(pick.on);
                else document.getElementById(`${id}-date`)?.focus();
              }}
            >
              <span className={styles.pickLabel}>{pick.label}</span>
              {pick.on !== null ? <span className={styles.pickDate}>{shortDay(pick.on)}</span> : null}
            </button>
          );
        })}
      </div>
      <div className={styles.calendar}>
        <div className={styles.monthHead}>
          <span className={styles.monthLabel}>{monthLabel(view.year, view.month)}</span>
          <span className={styles.monthNav}>
            <button
              type="button"
              className={styles.monthButton}
              aria-label="Vorheriger Monat"
              onClick={() => shiftMonth(-1)}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.monthButton}
              aria-label="Nächster Monat"
              onClick={() => shiftMonth(1)}
            >
              ›
            </button>
          </span>
        </div>
        <div className={styles.grid} role="grid" aria-label={monthLabel(view.year, view.month)}>
          {WEEKDAY_SHORT.map((day) => (
            <span key={day} className={styles.weekday} role="columnheader">
              {day}
            </span>
          ))}
          {cells.map((cell, index) =>
            cell.key === null ? (
              <span key={`empty-${index}`} className={styles.dayEmpty} aria-hidden="true" />
            ) : (
              <button
                key={cell.key}
                type="button"
                role="gridcell"
                aria-selected={cell.key === dueOn}
                aria-label={cell.key}
                className={clsx(
                  styles.day,
                  cell.key === dueOn && styles.daySelected,
                  cell.key === today && cell.key !== dueOn && styles.dayToday,
                )}
                onClick={() => setDueOn(cell.key ?? '')}
              >
                {cell.day}
              </button>
            ),
          )}
        </div>
        <div className={styles.fields}>
          <input
            id={`${id}-date`}
            type="text"
            inputMode="numeric"
            aria-label="Datum"
            placeholder="TT.MM.JJJJ"
            className={clsx(styles.field, styles.fieldDate)}
            value={dateText}
            onChange={(event) => changeDateText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
          <input
            type="text"
            inputMode="numeric"
            aria-label="Uhrzeit"
            placeholder="HH:MM"
            className={clsx(styles.field, styles.fieldTime)}
            value={dueTime}
            onChange={(event) => setDueTime(event.target.value)}
            onBlur={() => setDueTime((value) => normalizeTime(value))}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
        </div>
        {message || error ? (
          <div className={styles.error} role="alert">
            {message ?? error}
          </div>
        ) : null}
        <div className={styles.footer}>
          <button type="button" className={styles.cancel} onClick={onCancel} disabled={pending}>
            Abbrechen
          </button>
          <button
            type="button"
            className={styles.submit}
            onClick={submit}
            aria-busy={pending || undefined}
            disabled={pending}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface SnoozePopoverProps extends SnoozeFormProps {
  /** The toggle button; a click on it closes the popover through its own handler, not the outside click. */
  triggerRef?: RefObject<HTMLElement | null> | undefined;
  /** Positioned frame: `row` hangs 6 px above the row's bottom edge at its right padding (TQ-02); `end` below the trigger. */
  placement?: 'row' | 'end' | undefined;
}

/** The popover panel of TQ-02: absolute inside the row (which is `position: relative`), 268 px wide. */
export function SnoozePopover({ triggerRef, placement = 'row', onCancel, ...form }: SnoozePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, true, { initialFocus: 'first' });
  useEscapeKey(onCancel, true);
  useOutsideClick(triggerRef ? [ref, triggerRef] : [ref], onCancel, true);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Verschieben auf"
      tabIndex={-1}
      className={clsx(styles.popover, placement === 'end' && styles.popoverEnd)}
    >
      <SnoozeForm onCancel={onCancel} {...form} />
    </div>
  );
}
